<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

// QR login for personal accounts connected through a bridge (Telegram, MAX).
// Both bridges expose the same qr_start/qr_status/qr_password/qr_complete
// API, so only the API client and the translation block differ.
const props = defineProps({
  apiClient: { type: Object, required: true },
  i18nPrefix: { type: String, required: true },
  qrAlt: { type: String, required: true },
});

const { t } = useI18n();
const tr = (key, params) => t(`${props.i18nPrefix}.${key}`, params);

// idle -> pending (QR shown) -> need_password (2FA) -> authorized -> redirect
const status = ref('idle');
const qrImage = ref('');
const qrExpiresIn = ref(null);
const password = ref('');
const isSubmittingPassword = ref(false);
// Set by the bridge alongside need_password when the previous password was
// wrong — the MAX bridge checks it in the background and reports back here.
const errorMessage = ref('');

// The bridge can hold many independent accounts at once (one per KAM); this
// id ties every call in this connection attempt to its own session so
// accounts never cross-talk. crypto.randomUUID is only
// defined in secure contexts (https/localhost) — self-hosted installs
// served over plain http on a domain/IP need a fallback, or this throws
// during setup and the wizard never renders.
function generateSessionId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `s${Date.now()}${Math.random().toString(36).slice(2, 10)}`;
}

const sessionId = generateSessionId();

let pollTimer = null;

// The messenger, not us, decides how long a QR token lives, so the bridge
// reports the remaining seconds (qr_expires_in) and refreshes the code
// itself when it runs out. This only mirrors that — the fallback is for a
// bridge too old to send the field.
const QR_FALLBACK_SECONDS = 30;
const qrTotalSeconds = ref(QR_FALLBACK_SECONDS);
const qrSecondsLeft = ref(QR_FALLBACK_SECONDS);
let countdownTimer = null;

function stopCountdown() {
  if (countdownTimer) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
}

function restartCountdown(seconds) {
  stopCountdown();
  qrTotalSeconds.value = seconds || QR_FALLBACK_SECONDS;
  qrSecondsLeft.value = qrTotalSeconds.value;
  countdownTimer = setInterval(() => {
    if (qrSecondsLeft.value > 0) qrSecondsLeft.value -= 1;
  }, 1000);
}

watch(qrImage, newImage => {
  if (newImage) restartCountdown(qrExpiresIn.value);
});

// Anything the bridge/backend didn't return as one of these is treated as an
// error instead of silently leaving the wizard on a blank screen (e.g. the
// bridge restarted and forgot this session_id, or Rails 500'd).
const KNOWN_STATUSES = [
  'idle',
  'pending',
  'need_password',
  'authorized',
  'error',
];

function goToAddAgents(inboxId) {
  router.replace({
    name: 'settings_inboxes_add_agents',
    params: { page: 'new', inbox_id: inboxId },
  });
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  stopCountdown();
}

function applyStatus(data) {
  status.value = KNOWN_STATUSES.includes(data.status) ? data.status : 'error';
  qrExpiresIn.value = data.qr_expires_in;
  errorMessage.value = data.error || '';
  // Cleared when the bridge stops sending one (e.g. while a 2FA password is
  // being checked), so an expired code never reappears on screen.
  qrImage.value = data.qr_png_base64
    ? `data:image/png;base64,${data.qr_png_base64}`
    : '';
  return status.value;
}

// qr_start/qr_status/qr_password only report bridge state — creating (or
// reconnecting) the channel/inbox is a separate, explicit step so a GET
// that's polled every couple seconds can't double-create records or leak a
// raw 500 on a phone-number collision.
async function completeConnection() {
  stopPolling();
  try {
    const { data } = await props.apiClient.qrComplete(sessionId);
    goToAddAgents(data.inbox_id);
  } catch (error) {
    status.value = 'error';
    useAlert(error.response?.data?.error || tr('ERROR_MESSAGE'));
  }
}

async function pollStatus() {
  try {
    const { data } = await props.apiClient.qrStatus(sessionId);
    const resolvedStatus = applyStatus(data);

    if (resolvedStatus === 'authorized') {
      completeConnection();
    } else if (resolvedStatus === 'error') {
      stopPolling();
      useAlert(data.error || tr('ERROR_MESSAGE'));
    }
  } catch (error) {
    stopPolling();
    useAlert(tr('ERROR_MESSAGE'));
  }
}

function startPolling(intervalMs) {
  stopPolling();
  pollTimer = setInterval(pollStatus, intervalMs);
}

async function startQrLogin() {
  try {
    const { data } = await props.apiClient.qrStart(sessionId);
    const resolvedStatus = applyStatus(data);
    // The MAX bridge answers "idle" for the first moment, while it is still
    // fetching the QR from MAX — keep polling until the code shows up.
    if (resolvedStatus === 'pending' || resolvedStatus === 'idle') {
      startPolling(2000);
    } else if (resolvedStatus === 'authorized') {
      completeConnection();
    }
  } catch (error) {
    status.value = 'error';
    useAlert(tr('ERROR_MESSAGE'));
  }
}

async function submitPassword() {
  isSubmittingPassword.value = true;
  try {
    const { data } = await props.apiClient.qrPassword(
      sessionId,
      password.value
    );
    const resolvedStatus = applyStatus(data);
    if (resolvedStatus === 'authorized') {
      await completeConnection();
    }
  } catch (error) {
    useAlert(tr('ERROR_MESSAGE'));
  } finally {
    isSubmittingPassword.value = false;
  }
}

onMounted(startQrLogin);
onBeforeUnmount(stopPolling);
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader :header-title="tr('TITLE')" :header-content="tr('DESC')" />

    <div class="flex flex-col items-center mt-6">
      <template v-if="status === 'idle' || (status === 'pending' && !qrImage)">
        <p class="text-n-slate-11">
          {{ tr('LOADING') }}
        </p>
      </template>

      <template v-else-if="status === 'pending'">
        <img
          :src="qrImage"
          :alt="qrAlt"
          class="w-64 h-64 border border-n-weak rounded-lg"
        />
        <div class="w-64 h-1 mt-3 rounded-full bg-n-slate-4 overflow-hidden">
          <div
            class="h-full rounded-full bg-n-brand transition-all duration-1000 ease-linear"
            :style="{ width: `${(qrSecondsLeft / qrTotalSeconds) * 100}%` }"
          />
        </div>
        <p class="text-n-slate-11 text-xs mt-1">
          {{
            tr('QR_EXPIRES_IN', {
              seconds: qrSecondsLeft,
            })
          }}
        </p>
        <p class="text-n-slate-11 mt-4 max-w-sm text-center">
          {{ tr('QR_HINT') }}
        </p>
      </template>

      <template v-else-if="status === 'need_password'">
        <form class="w-full max-w-xs" @submit.prevent="submitPassword">
          <p v-if="errorMessage" class="text-n-ruby-9 mb-2">
            {{ errorMessage }}
          </p>
          <label>
            {{ tr('PASSWORD.LABEL') }}
            <input
              v-model="password"
              type="password"
              :placeholder="tr('PASSWORD.PLACEHOLDER')"
            />
          </label>
          <NextButton
            class="mt-4"
            :is-loading="isSubmittingPassword"
            type="submit"
            solid
            blue
            :label="tr('PASSWORD.SUBMIT')"
          />
        </form>
      </template>

      <template v-else-if="status === 'error'">
        <p class="text-n-ruby-9">
          {{ tr('ERROR_MESSAGE') }}
        </p>
        <NextButton
          class="mt-4"
          outline
          :label="tr('RETRY')"
          @click="startQrLogin"
        />
      </template>
    </div>
  </div>
</template>
